import React from "react";
import { motion } from "framer-motion";

function Features() {
  const features = [
    {
      title: "Connected & Intelligent Manufacturing",
      description:
        "Create fully connected factories using IoT, cloud, and AI to enable real-time data sharing, smarter decision-making, and streamlined production processes.",
      color: "#1E88E5",
    },
    {
      title: "Digital Twin Technology",
      description:
        "Leverage virtual replicas of physical assets to simulate, monitor, and optimize operations, reducing downtime and improving product quality.",
      color: "#43A047",
    },
    {
      title: "Predictive Analytics & Maintenance",
      description:
        "Use AI-driven analytics to forecast potential equipment failures, optimize maintenance schedules, and ensure continuous production flow.",
      color: "#FB8C00",
    },
    {
      title: "Data-Driven Process Optimization",
      description:
        "Analyze production data to identify bottlenecks, reduce waste, and maximize efficiency across every stage of the manufacturing cycle.",
      color: "#8E24AA",
    },
    {
      title: "Seamless IT & OT Integration",
      description:
        "Integrate operational technology with IT systems for unified visibility, enabling faster responses to production changes and market demands.",
      color: "#00ACC1",
    },
    {
      title: "Sustainable & Energy-Efficient Manufacturing",
      description:
        "Implement eco-friendly digital solutions to reduce energy usage, cut emissions, and meet global sustainability standards.",
      color: "#F4511E",
    },
  ];

  // Smooth animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.35, // smoother delay between cards
        duration: 1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: "easeInOut" }, // smoother effect
    },
  };

  const styles = {
    container: {
      padding: "60px 20px",
      textAlign: "center",
      background: "#f8f9fa",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    },
    heading: {
      fontSize: "32px",
      marginBottom: "40px",
      fontWeight: "bold",
      letterSpacing: "0px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "25px",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    card: {
      background: "#fff",
      borderRadius: "10px",
      boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
      padding: "25px",
      textAlign: "left",
      transition: "all 0.3s ease",
      borderLeft: "8px solid transparent",
      minHeight: "200px",
      cursor: "pointer",
    },
    title: {
      fontSize: "18px",
      fontWeight: "600",
      marginBottom: "15px",
      color: "#1e40af",
    },
    desc: {
      fontSize: "15px",
      lineHeight: "1.6",
      textAlign: "justify",
      color: "#4b5563",
    },
  };

  return (
    <div style={styles.container}>
      <motion.h2
        style={styles.heading}
        initial={{ opacity: 0, y: -40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1, ease: "easeInOut" }}
      >
        Key Features of Our Digital Factory
      </motion.h2>

      <motion.div
        style={styles.grid}
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {features.map((feature, index) => (
          <motion.div
            key={index}
            variants={cardVariants}
            style={{
              ...styles.card,
              borderLeft: `8px solid ${feature.color}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderLeft = `16px solid ${feature.color}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderLeft = `8px solid ${feature.color}`;
            }}
          >
            <h3 style={styles.title}>{feature.title}</h3>
            <p style={styles.desc}>{feature.description}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

export default Features;
